import { FC, useMemo } from 'react';
import { Box } from '@mui/material';
import classNames from 'classnames';
import { IUserPatientDto } from '~/services/api/users/types';
import styles from './PatientList.module.scss';

interface IProps {
  patients: IUserPatientDto[];
  selectedIds: number[];
  disabledIds?: number[];
  onToggle: (id: number) => void;
  isTargetEmpty?: boolean;
}

const PatientList: FC<IProps> = ({
  patients,
  selectedIds,
  disabledIds = [],
  onToggle,
  isTargetEmpty,
}) => {
  const sortedPatients = useMemo(
    () =>
      [...patients].sort((a, b) => {
        const aDisabled = disabledIds.includes(a.id) ? 1 : 0;
        const bDisabled = disabledIds.includes(b.id) ? 1 : 0;
        return aDisabled - bDisabled;
      }),
    [patients, disabledIds],
  );

  if (isTargetEmpty) {
    return (
      <Box className={styles.listContainer}>
        <Box className={styles.emptyState}>
          Select a health manager to view their assigned patients.
        </Box>
      </Box>
    );
  }

  if (patients.length === 0) {
    return (
      <Box className={styles.listContainer}>
        <Box className={styles.emptyState}>No patients found</Box>
      </Box>
    );
  }

  return (
    <Box className={styles.listContainer}>
      {sortedPatients.map(patient => {
        const isDisabled = disabledIds.includes(patient.id);
        const isSelected = selectedIds.includes(patient.id);

        const itemClass = classNames(styles.patientItem, {
          [styles.selected]: isSelected,
          [styles.disabled]: isDisabled,
        });

        return (
          <Box
            key={patient.id}
            title={`${patient.fullName} - ${patient.programName}${
              isDisabled ? ' (Both assigned)' : ''
            }`}
            className={itemClass}
            onClick={() => !isDisabled && onToggle(patient.id)}
          >
            <Box className={styles.name}>
              {patient.fullName}
              {isDisabled && <span className={styles.status}>(Already assigned)</span>}
            </Box>
            <Box className={styles.program}>{patient.programName}</Box>
          </Box>
        );
      })}
    </Box>
  );
};

export default PatientList;
